/**
 * What the team chat will not do, decided before anything is recorded.
 *
 * The chat only sets up a team. A message asking for anything else (a plan
 * upgrade, billing, a change to MARS's code or data, someone's access, "ignore
 * your instructions") gets a plain answer saying what it cannot do and where
 * to go, and the same question again; it never becomes an answer. The deal
 * desk does the same on the server: mars-backend/services/agentTeam/guard.js.
 * Keep the two alike.
 *
 * The patterns are narrow on purpose: setup words ("payment methods", "a bonus
 * code", "Affiliate Manager") must never trip them.
 */

const TOPICS = [
  {
    key: 'plan',
    rx: new RegExp([
      String.raw`\b(?:upgrade|downgrade)\b(?=[^.?!\n]{0,40}\b(?:plan|account|subscription|enterprise|membership|me|us)\b)`,
      String.raw`\b(?:enterprise|pro|premium|paid|pricing|current|free)\s+plan\b`,
      String.raw`\b(?:my|our|the account'?s?)\s+(?:plan|subscription|membership)\b`,
      // The account's own subscription, billing and card, never a deal's
      // ("deposits by debit card", "affiliates send an invoice", "monthly billing on NGR").
      String.raw`\b(?:my|our|the|mars'?s?|account'?s?) subscription\b`, String.raw`\bsubscription (?:plan|tier|level|fee|price|renewal)\b`,
      String.raw`\b(?:my|our|mars'?s?|account) billing\b`, String.raw`\bbilling (?:details|info|information|address|page|settings|account)\b`,
      String.raw`\b(?:my|our) invoices?\b`, String.raw`\b(?:send|show|give|get|download) (?:me|us) (?:my |our |the |an? )?invoices?\b`,
      String.raw`\b(?:my|our|update (?:my |our |the )?|change (?:my |our |the )?)(?:credit |debit |payment )?card\b`,
      String.raw`\bcancel (?:my|our|the) (?:account|subscription|membership)\b`
    ].join('|'), 'i'),
    reply: 'I can\'t change your plan, subscription or billing from this chat. The Pricing page shows the plans, and your account admin or MARS support can change yours.'
  },
  {
    key: 'code',
    rx: new RegExp([
      // "your code", "the app's code", never "a bonus code" or "an exclusive code".
      String.raw`\b(?:source|backend|frontend|app'?s|your|mars'?s?)\s+code\b`,
      String.raw`\bcode ?base\b`,
      String.raw`\b(?:change|changes|edit|edits|modify)\s+(?:in|to)\s+(?:the\s+)?code\b`,
      // "change the code so teams start on their own", but not "change the code to SWIFT50" (a bonus code).
      String.raw`\b(?:change|edit|modify|rewrite|update|fix)\s+the\s+code\s*(?:$|[.?!,]|so\b|that\b|which\b|to (?:make|let|stop|add|allow|remove|change)\b)`,
      String.raw`\b(?:change|edit|modify|rewrite|update|fix|hack|patch|rebuild)\s+(?:the |your |this |mars'?s? )?(?:app|application|software|system|platform|database|server|backend|frontend)\b`,
      String.raw`\b(?:sql|database|db)\s+(?:query|queries|table|tables|record|records|row|rows)\b`, String.raw`\bdrop table\b`,
      String.raw`\bdeploy\b`, String.raw`\bgit push\b`, String.raw`\bapi keys?\b`, String.raw`\benv(?:ironment)? variables?\b`, String.raw`\.env\b`,
      String.raw`\b(?:run|execute)\s+(?:a |this |the |some )?(?:script|command|query|shell)\b`,
      String.raw`\badd (?:a )?(?:new )?feature\b`
    ].join('|'), 'i'),
    reply: 'I can\'t change MARS itself from this chat: its code, its data, its settings or how it works. Tell MARS support what you need and the team will look at it.'
  },
  {
    key: 'access',
    rx: new RegExp([
      String.raw`\b(?:make me|set me as|give me|grant me)\b[^.?!\n]{0,20}\b(?:admin|owner|access|permissions?|rights)\b`,
      String.raw`\badmin (?:access|rights|panel|account)\b`,
      String.raw`\breset (?:my |the |their |his |her )?password\b`,
      String.raw`\b(?:other|another) (?:users?|accounts?|customers?)\b`,
      String.raw`\bdelete (?:my|the|our|all|every)\s+(?:account|teams?|users?|data|everything)\b`
    ].join('|'), 'i'),
    reply: 'I can\'t change accounts, access or passwords, or delete anything, from this chat. Your account admin can, in the User Panel.'
  },
  {
    // Lookups in MARS's records: "how many users are in the DB", "is sam@site.com a user".
    // Deal talk about players ("new users get 100 free spins") must pass.
    key: 'data',
    rx: new RegExp([
      String.raw`\b(?:users?|accounts?|customers?|members?|emails?|records?)\b[^.?!\n]{0,25}\b(?:in|on|from|of)\s+(?:the |your |our |mars'?s? )?(?:db|database|system|records)\b`,
      String.raw`\b(?:how many|total(?: number of)?|number of|count (?:of )?(?:the )?)\s*(?:registered |active |signed[- ]up )?(?:users|accounts|customers|members)\b`,
      String.raw`\b(?:list|show|give|get|export)\s+(?:me\s+)?(?:all|every|the)\s+(?:the\s+)?(?:registered\s+)?(?:users|accounts|customers|members)\b`,
      String.raw`\busers? (?:table|list|data|details)\b`,
      String.raw`\b(?:check|see|find out|look up|tell me|verify)\b[^.?!\n]{0,25}\b(?:if|whether)\b[^.?!\n]{0,40}\b(?:user|account|member|customer)\b[^.?!\n]{0,30}\b(?:exists?|available|registered|signed up|there|active)\b`,
      String.raw`\b(?:look ?up|search for|find)\s+(?:the |this |a |that )?(?:user|account|member|customer)\b`,
      String.raw`\b(?:is|does)\s+\S+@\S+\s+(?:a |an )?(?:user|member|customer|registered|exist|have an account|on mars)\b`,
      String.raw`\b(?:check|see|verify|find out|tell me)\b[^.?!\n]{0,30}\S+@\S+[^.?!\n]{0,20}\b(?:registered|exists?|a user|an account|signed up|on mars)\b`
    ].join('|'), 'i'),
    reply: 'I can\'t look up users, accounts or anything else in MARS\'s records from this chat. Your account admin can see your users in the User Panel.'
  },
  {
    key: 'injection',
    rx: new RegExp([
      String.raw`\bignore (?:all |any |the |your )?(?:previous |prior |above |earlier |system )?(?:instructions|rules|prompts?|guidelines)\b`,
      String.raw`\bsystem prompt\b`, String.raw`\bdeveloper mode\b`, String.raw`\bjailbreak\b`, String.raw`\byou are now\b`,
      String.raw`\bpretend (?:you are|to be)\b`, String.raw`\bact as (?:an? |the )?(?:admin|developer|system|root)\b`
    ].join('|'), 'i'),
    reply: 'I\'ll keep to what this chat is for.'
  }
];

/** The topic a message is about when this chat must not handle it: { key, reply }; null when it is fine. */
export function outOfScope(message) {
  const text = String(message || '');
  if (!text.trim()) return null;
  const hit = TOPICS.find((t) => t.rx.test(text));
  return hit ? { key: hit.key, reply: hit.reply } : null;
}
